import * as _ from 'lodash';
import { useEffect } from 'react';
import type { VoidFunctionComponent } from 'react';

import Locator from '../../../../shared/Locator';
import { FormGroup, FormLabelVariant, Icon, icons, LabeledMultiTextInput, LabeledSelect, Select } from '../../generic';
import Button from '../../generic/Button';
import InfoPanel from '../../generic/InfoPanel';
import Loader from '../../generic/Loader';
import type { PackageLabelAssignmentWithName } from '../../../../shared/model-typings/packagelabelassignment';
import type { PackageLabelScope } from '../../../../shared/packageLabels';
import type { SelectOption } from '../../../types';

import { Tab } from './types';
import type { LabelOption, VersionedLabelOption } from './types';
import type { usePackageLabelState } from './usePackageLabelState';

import styles from './ModalManagePackageLabels.module.less';

const TabScopes: Record<Tab, PackageLabelScope> = {
  [Tab.Revision]: 'revision',
  [Tab.Project]: 'project',
  [Tab.Global]: 'org',
};

const AllLabelsSelected: VoidFunctionComponent<{ inputValue: string }> = () => (
  <span>All available labels have been selected. Create new labels in your organization settings.</span>
);

const PermissionPanel: VoidFunctionComponent = () => (
  <InfoPanel>
    You do not have permission to make changes here.
  </InfoPanel>
);

type Props = {
  tab: Tab;
  activeTab: Tab;
  helpText: string;
  parentRevisionLocator?: string;
  state: ReturnType<typeof usePackageLabelState>;
  labelsSelected: VersionedLabelOption;
  setLabelsSelected: (labels: VersionedLabelOption) => void;
  hasNoPermission: boolean;
};

const TabContent: VoidFunctionComponent<Props> = ({
  tab,
  activeTab,
  helpText,
  parentRevisionLocator,
  state,
  labelsSelected,
  setLabelsSelected,
  hasNoPermission,
}) => {
  useEffect(() => {
    state.initializeGlobalSelected();
    state.initializeProjectSelected(Locator.parsePackageString(parentRevisionLocator!));
    state.initializeRevisionSelected(parentRevisionLocator!);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- we only want to refresh this effect when the label assignments change
  }, [state.labelAssignments]);

  if (tab !== activeTab) {
    return null;
  }

  if (state.isLoadingLabels || state.isLoadingLabelAssignments) {
    return <Loader />;
  }

  // Global tab is the only tab that should be visible to users without permission
  if (hasNoPermission && tab !== Tab.Global) {
    return <PermissionPanel />;
  }

  const labelMap = _.keyBy(state.labels?.packageLabels, (label) => label.id);
  const labelOptions = state.labels?.packageLabels?.map((label) => ({ value: label.id, label: label.name }));

  const isScopeMatch = (assignment: PackageLabelAssignmentWithName) => {
    return assignment.scope === TabScopes[tab];
  };

  const isParentProjectMatch = (assignment: PackageLabelAssignmentWithName) => {
    if (TabScopes[tab] !== 'project') {
      return true;
    }
    return assignment.scopeId === null || assignment.scopeId === Locator.parsePackageString(parentRevisionLocator!);
  };

  const isParentRevisionMatch = (assignment: PackageLabelAssignmentWithName) => {
    if (TabScopes[tab] !== 'revision') {
      return true;
    }
    return assignment.scopeId === null || assignment.scopeId === parentRevisionLocator;
  };

  /**
   * Get all matching assignments for the current scope and package from the initial state.
   */
  const initialAssignments = state.labelAssignments?.packageLabelAssignments?.filter(
    (assignment) => isScopeMatch(assignment) && isParentProjectMatch(assignment) && isParentRevisionMatch(assignment),
  );

  /**
   * Partition the assignments into all version and specific version assignments to be used for default values.
   */
  const [allVersionInitialAssignments, specificVersionInitialAssignments] = _.partition(
    initialAssignments,
    (assignment) => assignment.packageVersion == null,
  );

  /**
   * Create the default values for the all-version select.
   */
  const defaultAllVersionAssignments: LabelOption[] = allVersionInitialAssignments.map((assignment) => ({
    value: assignment.labelId,
    label: labelMap[assignment.labelId]?.name ?? '',
  }));

  /**
   * Create the default values for the specific version selects.
   *
   * _.groupBy() groups the assignments by package version.
   * _.mapValues() iterates over the grouped assignments and creates an array of label options for each version.
   */
  const defaultSpecificVersionAssignments: Record<string, LabelOption[]> = _.mapValues(
    _.groupBy(specificVersionInitialAssignments, (assignment) => assignment.packageVersion),
    (assignmentGroup) => {
      return assignmentGroup.map((assignment) => ({
        value: assignment.labelId,
        label: labelMap[assignment.labelId]?.name ?? '',
      }));
    },
  );

  /**
   * Add a new specific-version select with an empty set of labels as a placeholder.
   * Setting it to an empty string allows the hash map deduplication to force
   * only one "empty" key to be added or displayed.
   */
  const onClickAddVersion = () => {
    setLabelsSelected({ ...labelsSelected, '': new Set() });
  };

  /**
   * Remove a specific version version from the selected labels state.
   * This both removes the element from the UI and prevents it from
   * being persisted if the user saves the changes.
   */
  const onClickRemoveVersion = (version: string) => {
    const newLabelsSelected = { ...labelsSelected };
    delete newLabelsSelected[version];
    setLabelsSelected(newLabelsSelected);
  };

  /**
   * Update the selected labels state for a specific version (or all versions).
   *
   * @param selectedLabels - The labels that the user has selected.
   * @param rawVersion - The version header for the input that the user is changing.
   */
  const onChange = (selectedLabels: Array<SelectOption<number>>, rawVersion: string | null = null) => {
    const version = rawVersion ?? 'all';
    let versionMap: VersionedLabelOption = {};

    if (_.isEmpty(selectedLabels)) {
      // remove all labels for this version if the user clears the selection
      versionMap = { [version]: new Set() };
    } else {
      // reconcile the new and existing selections
      versionMap = selectedLabels.reduce((acc, label) => {
        return {
          ...acc,
          [version]: (acc[version] || new Set()).add(label.value),
        };
      }, {} as VersionedLabelOption);
    }
    setLabelsSelected({ ...labelsSelected, ...versionMap });
  };

  /**
   * Replace a specific version with a new version without losing the labels that were assigned to the old version.
   *
   * @param oldVersion - The version that the user is replacing.
   * @param newVersion - The new version that the user is replacing it with.
   */
  const onChangeVersion = (oldVersion: string, newVersion?: string) => {
    const newLabelsSelected = { ...labelsSelected };
    delete newLabelsSelected[oldVersion];
    if (newVersion) {
      setLabelsSelected({ ...newLabelsSelected, [newVersion]: new Set() });
    } else {
      setLabelsSelected(newLabelsSelected);
    }
  };

  /**
   * Get the correctly typed default values for the specific version select type, filtering out any assignments that
   * are scoped to all versions.
   *
   * @param version - The version that the user is getting the default values for.
   */
  const getDefaultValue = (version: string) => {
    if (version === 'all') {
      return defaultAllVersionAssignments;
    }
    return defaultSpecificVersionAssignments[version]
      ? defaultSpecificVersionAssignments[version]
      : [];
  };

  /**
   * Render the specific-version input for a given version, excluding the all-versions input.
   *
   * @param version - The version that the user is getting the input for.
   */
  const specificVersionInput = (version: string) => {
    if (version === 'all') {
      return null;
    }
    return (
      <div key={version}>
        {/*
          We're using a multi-select with a limit of 1 to ensure that the user
          1. can only select one version at a time
          2. the change handler isn't triggered until the user writes the entire version string
        */}
        <LabeledMultiTextInput
          id="specific-version"
          label="Specific Version"
          values={version ? [version] : []}
          maxItems={1}
          placeholder="Package version to assign labels to"
          onChange={(items: string[]) => onChangeVersion(version, items[0] as string)}
          size="small"
          labelVariant={FormLabelVariant.Filter}
          isDisabled={hasNoPermission}
        />
        <Select
          id={`specific-version-labels-${version}`}
          isMulti
          onChange={(selectedLabels: LabelOption[]) => onChange(selectedLabels, version)}
          options={labelOptions}
          defaultValue={getDefaultValue(version)}
          closeMenuOnSelect
          noOptionsMessage={AllLabelsSelected}
          placeholder="Assign labels..."
          isDisabled={hasNoPermission}
        />
        <div className={styles.removeButton}>
          <Button
            title="Remove"
            onClick={() => onClickRemoveVersion(version)}
            type="text"
            size="medium"
            isDisabled={hasNoPermission}
          >
            <span className={styles.removeText}>
              <Icon icon={icons.xCircle} />
              Remove
            </span>
          </Button>
        </div>
      </div>
    );
  };

  return (
    <>
      {hasNoPermission && <PermissionPanel />}
      <p className={styles.helpText}>{helpText}</p>
      <FormGroup>
        <LabeledSelect
          id="all-versions"
          label="All versions"
          isMulti
          onChange={(selectedVersions: LabelOption[]) => onChange(selectedVersions, null)}
          options={labelOptions}
          closeMenuOnSelect
          noOptionsMessage={AllLabelsSelected}
          placeholder="Assign labels..."
          defaultValue={defaultAllVersionAssignments}
          variant={FormLabelVariant.Filter}
          isDisabled={hasNoPermission}
        />
        {Object.keys(labelsSelected).map((version) => specificVersionInput(version))}
        <Button
          icon={icons.plusCircleO}
          title="Add Version"
          onClick={onClickAddVersion}
          type="text"
          size="medium"
          isDisabled={hasNoPermission}
        >
          Add Version
        </Button>
      </FormGroup>
    </>
  );
};

export default TabContent;
